import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConversationMessageItem } from '@wapharma/design-system';
import { apiDelete, apiGet, apiPost, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';

export type ConversationView = {
  id: string;
  audience: 'INTERNAL' | 'CLIENT';
  entityType: string | null;
  entityId: string | null;
  subject: string | null;
  status?: string;
  channel?: string | null;
  clientContact?: string | null;
  displayTitle?: string;
  displaySubtitle?: string | null;
  displayAvatarUrl?: string | null;
  lastMessageAt?: string | null;
  messages: ConversationMessageItem[];
  draftReferences?: ConversationReferenceItem[];
};

export type ConversationReferenceItem = {
  id: string;
  refType: string;
  refId: string;
  label: string | null;
  snapshot: Record<string, unknown>;
  createdByUserId: string | null;
  createdAt: string;
};

type EntityMode = {
  mode?: 'entity';
  entityType: string;
  entityId: string | null | undefined;
  audience: 'INTERNAL' | 'CLIENT';
};

type IdMode = {
  mode: 'id';
  conversationId: string | null | undefined;
};

type UseConversationOptions = (EntityMode | IdMode) & {
  token: string | null;
  enabled?: boolean;
  onMarkedRead?: () => void;
};

export function useConversation(options: UseConversationOptions) {
  const { token, enabled = true, onMarkedRead } = options;
  const isIdMode = options.mode === 'id';
  const conversationId = isIdMode ? options.conversationId : null;
  const entityType = isIdMode ? null : options.entityType;
  const entityId = isIdMode ? null : options.entityId;
  const audience = isIdMode ? null : options.audience;
  const [conversation, setConversation] = useState<ConversationView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onMarkedReadRef = useRef(onMarkedRead);

  onMarkedReadRef.current = onMarkedRead;

  const refresh = useCallback(async () => {
    if (!token || !enabled) return;

    if (isIdMode) {
      if (!conversationId) return;
      setLoading(true);
      setError(null);
      try {
        const detail = await apiGet<{ data: ConversationView }>(
          `/conversations/${conversationId}`,
          token,
        );
        setConversation(detail.data);
        await apiPost(`/conversations/${conversationId}/read`, {}, token);
        onMarkedReadRef.current?.();
      } catch (e: unknown) {
        setError(getDisplayError(e));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!entityType || !entityId || !audience) return;
    setLoading(true);
    setError(null);
    try {
      const resolved = await apiGet<{ data: { conversationId: string | null } }>(
        `/conversations/resolve?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}&audience=${encodeURIComponent(audience)}`,
        token,
      );
      const resolvedId = resolved.data?.conversationId ?? null;
      if (resolvedId) {
        const detail = await apiGet<{ data: ConversationView }>(`/conversations/${resolvedId}`, token);
        setConversation(detail.data);
        return;
      }
      setConversation(null);
    } catch (e: unknown) {
      setError(getDisplayError(e));
    } finally {
      setLoading(false);
    }
  }, [audience, conversationId, enabled, entityId, entityType, isIdMode, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const ensureConversation = useCallback(async () => {
    if (isIdMode) return conversation;
    if (!token || !entityType || !entityId || !audience) return null;
    if (conversation) return conversation;
    const created = await apiPost<{ data: ConversationView }>(
      '/conversations',
      { audience, entityType, entityId },
      token,
    );
    setConversation(created.data);
    return created.data;
  }, [audience, conversation, entityId, entityType, isIdMode, token]);

  const sendMessage = useCallback(
    async (input: {
      body: string;
      isInternal: boolean;
      channel?: 'EMAIL' | 'WHATSAPP';
      subject?: string | null;
      files: File[];
      references?: Array<{ refType: string; refId: string; label?: string | null }>;
    }): Promise<string | null> => {
      if (!token) return null;
      let conv = conversation;
      if (!conv?.id && !isIdMode) {
        conv = await ensureConversation();
      }
      if (!conv?.id) return null;

      const { files, ...payload } = input;
      if (files.length) {
        const fd = new FormData();
        fd.append('body', payload.body);
        fd.append('isInternal', payload.isInternal ? 'true' : 'false');
        if (payload.channel) fd.append('channel', payload.channel);
        if (payload.subject) fd.append('subject', payload.subject);
        if (payload.references?.length) {
          fd.append('references', JSON.stringify(payload.references));
        }
        for (const file of files) fd.append('files', file, file.name);
        await apiPostForm(`/conversations/${conv.id}/messages`, fd, token);
      } else {
        await apiPost(`/conversations/${conv.id}/messages`, payload, token);
      }
      await refresh();
      return conv.id;
    },
    [conversation, ensureConversation, isIdMode, refresh, token],
  );

  const addReference = useCallback(
    async (input: { refType: string; refId: string; label?: string | null }) => {
      if (!token || !conversation?.id) return;
      await apiPost(`/conversations/${conversation.id}/references`, input, token);
      await refresh();
    },
    [conversation?.id, refresh, token],
  );

  const removeReference = useCallback(
    async (referenceId: string) => {
      if (!token || !conversation?.id) return;
      await apiDelete(`/conversations/${conversation.id}/references/${referenceId}`, token);
      await refresh();
    },
    [conversation?.id, refresh, token],
  );

  return { conversation, loading, error, refresh, sendMessage, ensureConversation, addReference, removeReference };
}
